/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { DecsService } from '@shared/services/decs/decs.service';
import { combineLatest, map, Observable, of } from 'rxjs';
import { Weather } from './models/weather';
import { WeatherView } from './models/weather.view';
import { DecsResponse } from '@shared/services/decs/models/response/decs.response';
// import { ReleaseService } from '@flight-details/release/release.service';
import { DateTime } from 'luxon';
import { AirportWeatherView } from './models/airport-weather.view';
import { SabreTextLine } from '@shared/components/sabre-text-dialog/models/sabre-text-line';
import { StationType } from './models/station-type';
import { Station } from './models/station';

@Injectable({
  providedIn: 'root',
})
export class WeatherStore {
  private _stations?: Station[];
  private _depTimeUtc?: DateTime | null;
  private _arrivalTimeUtc?: DateTime | null;

  constructor(
    private decsService: DecsService,
    // private releaseService: ReleaseService,
    private alertService: AlertService,
  ) {}

  /** Get weather for all departure, destination, and alternate airports using cache strategy. */
  getWeather(legView: LegView, isRefresh = false) {
    const releaseRequest = legView.getReleaseRequest();

    if (!legView || !releaseRequest) {
      return of(
        new RequestState<WeatherView>(RequestStatus.IsCompleteNoResponse),
      );
    }

    this._depTimeUtc =
      legView.depTimeActualUTC ||
      legView.depTimeReschedUTC ||
      legView.depTimeSchedUTC ||
      null;

    this._arrivalTimeUtc =
      legView.arrTimeActualUTC ||
      legView.arrTimeReschedUTC ||
      legView.arrTimeSchedUTC ||
      null;

    this._stations = [
      { id: legView.depStation, type: 'dep' },
      { id: legView.arrStation, type: 'arr' },
    ];

    const requests = this.createRequests(this._stations, isRefresh);

    return combineLatest(requests).pipe(
      map((responses) => {
        return this.mapResponses(responses);
      }),
    );

    // return this.releaseService.getRelease(releaseRequest, isRefresh).pipe(
    //   map((releaseState) => {
    //     const stations: Station[] = [
    //       { id: legView.depStation, type: 'dep' },
    //       { id: legView.arrStation, type: 'arr' },
    //     ];
    //
    //     // Append alternate(s) airport to stations array.
    //     if (releaseState.response) {
    //       if (
    //         releaseState.response.alternate &&
    //         releaseState.response.alternate !== 'NONE'
    //       ) {
    //         stations.push({ id: releaseState.response.alternate, type: 'alt' });
    //       }
    //       if (releaseState.response.secondAlternate) {
    //         stations.push({
    //           id: releaseState.response.secondAlternate,
    //           type: 'alt',
    //         });
    //       }
    //       if (releaseState.response.takeoffAlternate) {
    //         stations.push({
    //           id: releaseState.response.takeoffAlternate,
    //           type: 'to_alt',
    //         });
    //       }
    //     }
    //
    //     this._stations = stations;
    //
    //     return this.createRequests(stations, isRefresh);
    //   }),
    //   switchMap((requests) => {
    //     return combineLatest(requests).pipe(
    //       map((responses) => {
    //         return this.mapResponses(responses);
    //       })
    //     );
    //   })
    // );
  }

  private mapResponses(
    responses: RequestState<Weather>[],
  ): RequestState<WeatherView> {
    let isLoading = false; // If any request is still loading.
    let hasAllResponses = true; // If all requests have a response.
    const weatherList: Weather[] = [];
    const airports: string[] = [];

    for (const responseState of responses) {
      if (responseState.isLoading) {
        isLoading = true;
      }

      if (responseState.response) {
        const airport = responseState.response.station;

        // Update airport list as needed.
        if (!airports.includes(airport.id)) {
          airports.push(airport.id);
        }

        weatherList.push(responseState.response);
      } else {
        hasAllResponses = false;
      }
    }

    // Return result when all responses are finished.
    if (hasAllResponses) {
      let timestamp = DateTime.now();

      if (responses.length > 0 && responses[0].response) {
        timestamp = DateTime.fromISO(responses[0].response.timestamp, {
          zone: 'utc',
        });
      }

      const weatherView = this.mapWeatherView(responses, timestamp, isLoading);

      // isLoading but with all responses.
      if (isLoading) {
        return new RequestState<WeatherView>(
          RequestStatus.IsLoadingWithCachedResponse,
          weatherView,
        );
      } else {
        return new RequestState<WeatherView>(
          RequestStatus.IsCompleteWithResponse,
          weatherView,
        );
      }
      // Still loading, only return loading state.
    } else if (isLoading) {
      return new RequestState<WeatherView>(RequestStatus.IsLoadingNoResponse);
    }

    // At least one request still loading.
    return new RequestState<WeatherView>(RequestStatus.IsCompleteNoResponse);
  }

  private mapWeatherView(
    responses: RequestState<Weather>[],
    timestamp: DateTime,
    isLoading: boolean,
  ): WeatherView {
    const weatherList: AirportWeatherView[] = [];

    // Create AirportWeatherView for each airport.
    for (const station of this._stations!) {
      const airportWeatherView: AirportWeatherView = {
        station,
      };

      for (const responseState of responses) {
        const { response } = responseState;

        if (response && response.station.id === station.id) {
          if (response.type === 'metar') {
            airportWeatherView.metar = response;
          } else if (response.type === 'taf') {
            airportWeatherView.taf = response;
          }
        }
      }

      weatherList.push(airportWeatherView);
    }

    return {
      timestamp,
      isLoading,
      weatherList,
    };
  }

  private createRequests(
    stations: Station[],
    isRefresh = false,
  ): Observable<RequestState<Weather>>[] {
    const requests: Observable<RequestState<Weather>>[] = [];

    for (const station of stations) {
      requests.push(this.getMetar(station, isRefresh));
      requests.push(this.getTaf(station, isRefresh));
    }

    return requests;
  }

  /** Get METAR using cache strategy. */
  private getMetar(
    station: Station,
    isRefresh: boolean,
  ): Observable<RequestState<Weather>> {
    return this.decsService
      .submitDecsRequest({ cmd: `SLS*${station.id}` }, isRefresh)
      .pipe(
        map((requestState) => {
          return this.mapMetarResponse(requestState, station);
        }),
      );
  }

  private mapMetarResponse(
    requestState: RequestState<DecsResponse>,
    station: Station,
  ): RequestState<Weather> {
    if (requestState.hasError) {
      this.alertService.setAlert({
        type: 'error',
        message: `Failed to get METAR for ${station.id}.`,
      });
    }

    if (!requestState.response) {
      return requestState.into<Weather>();
    }

    const weather = this.parseMetar(
      requestState.response.result,
      station,
      requestState.response.timestamp,
    );

    return requestState.into<Weather>(weather);
  }

  /** Split METAR into lines and remove unnecessary lines. */
  private parseMetar(
    text: string,
    station: Station,
    timestamp: string,
  ): Weather {
    const lines = text.split('\n').filter((line) => {
      const trimmed = line.trim();

      return (
        !trimmed.includes('RECVD FROM') &&
        !trimmed.includes('END DATA') &&
        !trimmed.match(/^[A-Z]{3} [A-Z]{4} \d{6}/) &&
        !(trimmed.match(/^\d{6}/) && trimmed.length === 6) &&
        !trimmed.includes('WSI/') &&
        !(trimmed.length === 0)
      );
    });

    // Occasionally a METAR will contain multiple reports. For simplicity, we
    // only display the first one. The start of a new report is indicated by
    // a date and zulu time (141611Z) starting the line.
    let reportCount = 0;
    const metarLines: string[] = [];

    for (let line of lines) {
      line = line.trim();

      if (line.match(/^\d{6}Z/)) {
        reportCount++;

        if (reportCount > 1) {
          break;
        }
      }
      metarLines.push(line);
    }

    const reportLines: SabreTextLine[] = [{ text: metarLines.join(' ') }];

    return {
      timestamp,
      station,
      lines: reportLines, // METAR should be displayed as one line.
      type: 'metar',
    };
  }

  /** Split TAF into lines and remove unnecessary lines. */
  private parseTaf(text: string, station: Station, timestamp: string): Weather {
    let lines = text.split('\n').filter((line) => {
      const trimmed = line.trim();

      return (
        !trimmed.includes('RECVD FROM') &&
        !trimmed.includes('END DATA') &&
        !(trimmed.match(/^\d{6}/) && trimmed.length === 6) &&
        !trimmed.includes('WSI/') &&
        !trimmed.startsWith('TAF/') &&
        !(trimmed.length === 0)
      );
    });

    // There are rare cases where a TAF is returned as one line. If a line is longer than it should be,
    // split on extra spaces.
    let splitLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length > 80 && lines.length === 1) {
        splitLines = lines[i]
          .split('   ')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);
      }
    }

    if (splitLines.length > 0) {
      lines = splitLines;
    }

    const formattedLines = this.formatTafLines(lines, station.type);

    return {
      timestamp,
      station,
      lines: formattedLines,
      type: 'taf',
    };
  }

  /** Mark TAF line as highlighted if the line is valid for the supplied date time. */
  private formatTafLines(
    lines: string[],
    stationType: StationType,
  ): SabreTextLine[] {
    // UTC date time of departure or arrival as appropriate.
    let refDateTime: DateTime | null = null;

    if (
      (stationType === 'dep' || stationType === 'to_alt') &&
      this._depTimeUtc
    ) {
      refDateTime = this._depTimeUtc;
    } else if (
      (stationType === 'alt' || stationType === 'arr') &&
      this._arrivalTimeUtc
    ) {
      refDateTime = this._arrivalTimeUtc;
    }

    let highlightIndex = -1; // The line index to apply highlighting to.
    const formattedLines: SabreTextLine[] = [];

    // First line of TAF. Extract valid start date/time. Example:
    //             ****
    // TAF 211652Z 2117/2212 15007KT P6SM SKC
    const firstLinePattern = /^TAF\s.*(?<start>\d{4})\/(?<validEnd>\d{4})/;

    // FM line pattern. Example:
    //   ******
    // FM220900 11003KT P6SM BKN012 OVC100
    const fromPattern = /^FM(?<start>\d{6})\s+/;

    // TEMPO or PROB30 or PROB40 or BECMG line pattern. Extract start and end.
    //       *********
    // TEMPO 2205/2209 -SHRASN BKN025
    const tempoPattern =
      /^(TEMPO|PROB30|PROB40|BECMG)\s+(?<start>\d{4})\/(?<end>\d{4})\s+/;

    // Fix embedded TEMPO/PROB30/etc.
    const fixedLines = this.fixEmbeddedTempo(lines);

    let validEndTime: DateTime | null = null;

    for (let i = 0; i < fixedLines.length; i++) {
      const line = fixedLines[i].trim();

      const match =
        line.match(firstLinePattern) ||
        line.match(fromPattern) ||
        line.match(tempoPattern);

      if (match && match.groups) {
        const start = match.groups['start'];
        const end = match.groups['end'];
        const validEnd = match.groups['validEnd']; // When TAF is valid through.
        let startDay;
        let startHour;
        let startDateTime: DateTime | null = null;
        let endDay;
        let endHour;
        let endDateTime: DateTime | null = null;

        if (start) {
          startDay = parseInt(start.substring(0, 2));
          startHour = parseInt(start.substring(2, 4));
          startDateTime = this.createTafDate(startDay, startHour);
        }

        // start will always exist, end may be null.
        if (end) {
          endDay = parseInt(end.substring(0, 2));
          endHour = parseInt(end.substring(2, 4));
          endDateTime = this.createTafDate(endDay, endHour);
        }

        if (validEnd) {
          const validEndDay = parseInt(validEnd.substring(0, 2));
          const validEndHour = parseInt(validEnd.substring(2, 4));
          validEndTime = this.createTafDate(validEndDay, validEndHour);
        }

        // Note the latest FM line that is after the ref time and within valid time.
        if (startDateTime && !endDateTime && validEndTime && refDateTime) {
          const startDelta = Math.round(
            refDateTime.diff(startDateTime, 'minutes')['minutes'],
          );
          const endDelta = Math.round(
            refDateTime.diff(validEndTime, 'minutes')['minutes'],
          );

          // FM line
          if (startDelta >= 0 && endDelta <= 0) {
            highlightIndex = i;
          }
        } else if (startDateTime && endDateTime && refDateTime) {
          // For TEMPO/PROB/BECMG where there is a start and end time. This takes priority over the FM lines
          // since it's more specific.
          const startDelta = Math.round(
            refDateTime.diff(startDateTime, 'minutes')['minutes'],
          );
          const endDelta = Math.round(
            refDateTime.diff(endDateTime, 'minutes')['minutes'],
          );

          if (startDelta >= 0 && endDelta <= 0) {
            highlightIndex = i;
          }
        }
      }

      formattedLines.push({ text: line });
    }

    if (highlightIndex >= 0) {
      formattedLines[highlightIndex].classes = ['highlight'];
    }

    return formattedLines;
  }

  /** Use the flight date to create a TAF date. */
  private createTafDate(tafDay: number, tafHour: number): DateTime {
    const now = DateTime.now().toUTC();

    let month = now.month;
    let year = now.year;

    // Adjust for month transition.
    if (tafDay - now.day > 25) {
      // TAF day is previous month.
      if (month === 1) {
        month = 12;
        year -= 1;
      } else {
        month -= 1;
      }
    } else if (now.day - tafDay > 25) {
      // TAF day is in next month.
      if (month === 12) {
        month = 1;
        year += 1;
      } else {
        month += 1;
      }
    }

    return DateTime.fromObject(
      {
        year,
        month,
        day: tafDay,
        hour: tafHour,
        minute: 0,
        second: 0,
      },
      {
        zone: 'UTC',
      },
    );
  }

  /** For some reason the TAFs from Sabre sometimes embed TEMPO/PROB30/etc. on the
   *  same line as a FM. Find these and put them on a separate line. */
  private fixEmbeddedTempo(lines: string[]): string[] {
    const fixedWords: string[] = [];

    // Inserting newlines in front of FM is a little different, since we want to only match FMXXXXXX (FM081200).
    const words = lines
      .join(' ')
      .split(' ')
      .filter((word) => word.length > 0);

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      // Add newline to FMXXXXXX (FM081200)
      if (/FM\d{6}/.test(word)) {
        fixedWords.push(`\n${word}`);
      } else if (word === 'BECMG' || word === 'PROB30' || word === 'PROB40') {
        fixedWords.push(`\n${word}`);
      } else if (word === 'TEMPO') {
        if (i > 0 && words[i - 1] !== 'PROB30' && words[i - 1] !== 'PROB40') {
          // Intl stations sometimes use PROB30 or PROB40 TEMPO together, so don't add newline if preceded by PROBXX.
          fixedWords.push(`\n${word}`);
        } else {
          fixedWords.push(word);
        }
      } else if (word.length > 0 && word !== 'undefined' && word !== 'FOS') {
        fixedWords.push(word);
      }
    }

    // Convert words array into lines array.
    return fixedWords.join(' ').split('\n');
  }

  /** Get TAF using cache strategy. */
  private getTaf(
    station: Station,
    isRefresh: boolean,
  ): Observable<RequestState<Weather>> {
    return this.decsService
      .submitDecsRequest({ cmd: `SLS*${station.id}/taf` }, isRefresh)
      .pipe(
        map((requestState) => {
          return this.mapTafResponse(requestState, station);
        }),
      );
  }

  private mapTafResponse(
    requestState: RequestState<DecsResponse>,
    station: Station,
  ): RequestState<Weather> {
    if (requestState.hasError) {
      this.alertService.setAlert({
        type: 'error',
        message: `Failed to get TAF for ${station.id}.`,
      });
    }

    if (!requestState.response) {
      return requestState.into<Weather>();
    }

    const weather = this.parseTaf(
      requestState.response.result,
      station,
      requestState.response.timestamp,
    );

    return requestState.into<Weather>(weather);
  }
}
